import { Component, ElementRef, HostListener, OnDestroy, OnInit, computed, effect, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { MarketplaceRefundRequestsService } from '../../../services/marketplace-refund-requests.service';
import { CategoryRequestsAdminService } from '../../../services/category-requests-admin.service';
import { BrandReviewAdminService } from '../../../services/brand-review-admin.service';

/** A single destination, shown in a section's flyout. */
export interface NavLink {
  path: string;
  label: string;
  icon: string;
  /** Only highlight when the URL matches exactly (section landing pages). */
  exact?: boolean;
  /** The server-resolved permission required to see the link. */
  permission: string;
  /** Shows a count next to the label (work waiting for this role). */
  badge?: 'refundRequests' | 'categoryRequests' | 'brandReview';
}

/** A tile on the rail. Clicking it opens the section's destinations in a flyout. */
export interface NavSection {
  id: string;
  label: string;
  /** SVG path data on a 24×24 grid, drawn as a 1.7px stroke. */
  icon: string;
  links: NavLink[];
}

/** Per-browser preference: whether the second level stays open as a column. */
const PINNED_KEY = 'plusAdmin.menuPinned';

/** Home sits on the rail as a direct destination, above the sections. */
export const HOME: NavLink = { path: '/home', label: 'Home', icon: 'M3 11l9-8 9 8M5 10v10h14V10M10 20v-6h4v6', exact: true, permission: 'Platform.Users.View' };

export const SECTIONS: NavSection[] = [
  {
    id: 'platform',
    label: 'Platform',
    icon: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
    links: [
      { path: '/users', label: 'Plus Users', icon: '👤', permission: 'Platform.Users.View' },
      { path: '/roles', label: 'Roles', icon: '🛡️', permission: 'Platform.Roles.ManagePermissions' },
      { path: '/permissions', label: 'Permissions', icon: '🔑', permission: 'Platform.Roles.ManagePermissions' },
      { path: '/businesses', label: 'Businesses', icon: '🏢', permission: 'Businesses.View' },
      { path: '/terminals', label: 'Terminals', icon: '🖥️', permission: 'Terminals.View' },
      { path: '/products', label: 'Products', icon: '📦', permission: 'Marketplace.Orders.View' },
      { path: '/notifications', label: 'Notifications', icon: '🔔', permission: 'Notifications.Diagnose' },
      { path: '/cache', label: 'Cache', icon: '🧹', permission: 'Platform.Cache.Evict' },
      { path: '/config-check', label: 'Config Check', icon: '🩺', permission: 'Platform.Config.View' },
      { path: '/maintenance', label: 'Maintenance', icon: '🚧', permission: 'Platform.Maintenance.Manage' },
      { path: '/websites/assigned', label: 'Assigned websites', icon: '🌐', permission: 'Sites.Assigned.View' },
      { path: '/websites/sites', label: 'Websites', icon: '🌐', permission: 'Sites.View' },
      { path: '/websites/settings', label: 'Website Settings', icon: '🛠️', permission: 'Sites.Settings.Manage' },
      { path: '/communications/providers', label: 'Messaging Providers', icon: '📨', permission: 'Platform.Secrets.Manage' },
      { path: '/image-sizes', label: 'Image Sizes', icon: '🖼️', permission: 'Platform.Images.Generate' },
      { path: '/report-flags', label: 'Reports (Platform)', icon: '🚦', permission: 'Platform.Settings.Manage' },
      { path: '/ticketing/seat-words', label: 'Ticket Seat Words', icon: '🪑', permission: 'Platform.Settings.Manage' },
      { path: '/catalog/unit-display', label: 'Unit of Measure', icon: '⚖️', permission: 'Platform.Settings.Manage' },
    ],
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    icon: 'M3 9l1.5-5h15L21 9M3 9h18v2a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0zM5 13v8h14v-8M10 21v-5h4v5',
    links: [
      { path: '/marketplace', label: 'Overview', icon: '🛒', exact: true, permission: 'Marketplace.Review.View' },
      { path: '/marketplace/applications', label: 'Seller Applications', icon: '📝', permission: 'Marketplace.Review.View' },
      { path: '/marketplace/compliance', label: 'Seller Compliance', icon: '✅', permission: 'Marketplace.Review.View' },
      { path: '/marketplace/orders', label: 'Orders', icon: '🧾', permission: 'Marketplace.Orders.View' },
      { path: '/marketplace/refund-requests', label: 'Refund Requests', icon: '↩️', permission: 'Marketplace.Orders.Refund', badge: 'refundRequests' },
      { path: '/marketplace/escrow', label: 'Escrow & Payouts', icon: '💰', permission: 'Marketplace.Escrow.View' },
      { path: '/marketplace/categories', label: 'Categories', icon: '🗂️', permission: 'Marketplace.Catalogue.Manage' },
      { path: '/marketplace/category-requests', label: 'Category Requests', icon: '📨', permission: 'Marketplace.Catalogue.Manage', badge: 'categoryRequests' },
      { path: '/marketplace/tags', label: 'Tags', icon: '🔖', permission: 'Marketplace.Catalogue.Manage' },
      { path: '/marketplace/brands', label: 'Brands', icon: '🏷️', permission: 'Marketplace.Catalogue.Manage' },
      { path: '/marketplace/brand-review', label: 'Brand Review', icon: '🔍', permission: 'Marketplace.Catalogue.Manage', badge: 'brandReview' },
      { path: '/marketplace/brand-coverage', label: 'Brand Coverage', icon: '📊', permission: 'Marketplace.Catalogue.Manage' },
      { path: '/marketplace/location-pages', label: 'Location Pages', icon: '📍', permission: 'Marketplace.Catalogue.Manage' },
      { path: '/marketplace/delivery-network', label: 'Delivery Network', icon: '🚚', permission: 'Marketplace.Orders.View' },
      { path: '/marketplace/delivery-countries', label: 'Delivery Countries', icon: '🌍', permission: 'Marketplace.Delivery.Manage' },
      { path: '/marketplace/operations', label: 'Operations', icon: '⚙️', permission: 'Marketplace.Sitemap.Rebuild' },
      { path: '/marketplace/search-analytics', label: 'Search Analytics', icon: '🔎', permission: 'Marketplace.Search.Analytics' },
      { path: '/marketplace/merchant-center', label: 'Google Shopping', icon: '🛍️', permission: 'Marketplace.Catalogue.Manage' },
      { path: '/marketplace/reason-codes', label: 'Reason Codes', icon: '🏁', permission: 'Marketplace.Catalogue.Manage' },
      { path: '/marketplace/promotions', label: 'Campaigns', icon: '🎟️', permission: 'Marketplace.Promotions.Manage' },
      { path: '/marketplace-tax', label: 'Tax', icon: '🏛️', permission: 'Marketplace.Tax.Manage' },
      { path: '/marketplace-users', label: 'Marketplace Users', icon: '👥', permission: 'Platform.Users.View' },
      { path: '/marketplace-customers', label: 'Marketplace Customers', icon: '🛍️', permission: 'Platform.Users.View' },
    ],
  },
  {
    id: 'fulfilment',
    label: 'Fulfilment',
    icon: 'M12 3l9 4.5v9L12 21l-9-4.5v-9zM3 7.5l9 4.5 9-4.5M12 12v9',
    links: [
      { path: '/fulfilment/facilities', label: 'Fulfilment centres', icon: '🏭', permission: 'Fulfilment.Programme.View' },
      { path: '/floor', label: 'Facility floor', icon: '📦', permission: 'Fulfilment.Floor.Use' },
      { path: '/fulfilment/returns', label: 'Returns', icon: '↩️', permission: 'Fulfilment.Programme.View' },
      { path: '/fulfilment/reimbursements', label: 'Reimbursements', icon: '🧾', permission: 'Fulfilment.Programme.View' },
      { path: '/fulfilment/balance-invoices', label: 'Balance invoices', icon: '⚖️', permission: 'Fulfilment.Programme.View' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: 'M3 10v4a1 1 0 0 0 1 1h2l5 4V5L6 9H4a1 1 0 0 0-1 1zM15 9a4 4 0 0 1 0 6M18 6a8 8 0 0 1 0 12',
    links: [
      { path: '/marketing/content', label: 'Content', icon: '📄', exact: true, permission: 'Marketing.Content.Edit' },
      { path: '/marketing/content/menus', label: 'Menus', icon: '🧭', permission: 'Marketing.Content.Edit' },
      { path: '/marketing/content/spaces', label: 'Spaces', icon: '🗂️', permission: 'Marketing.Content.Edit' },
      { path: '/marketing/content/products', label: 'Products', icon: '🏷️', permission: 'Marketing.Content.Edit' },
      { path: '/marketing/content/models', label: 'Models', icon: '🧩', permission: 'Marketing.Content.Edit' },
      { path: '/marketing/content/assets', label: 'Assets', icon: '🖼️', permission: 'Marketing.Content.Edit' },
      { path: '/marketing/campaigns', label: 'Email & SMS Campaigns', icon: '📣', exact: true, permission: 'Marketing.Campaign.Edit' },
      { path: '/marketing/campaigns/audiences', label: 'Audiences', icon: '👥', permission: 'Marketing.Campaign.Edit' },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: 'M2.5 6a2 2 0 0 1 2-2h15a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2zM2.5 10h19M6.5 15h3',
    links: [
      { path: '/subscriptions', label: 'Plans & Features', icon: '💳', permission: 'Billing.Plans.Manage' },
      { path: '/subscriptions/matrix', label: 'Plans × features', icon: '🧮', permission: 'Billing.Plans.Manage' },
      { path: '/payments', label: 'Payment Issues', icon: '🧯', exact: true, permission: 'Billing.Payments.View' },
      { path: '/payments/accounts', label: 'Payment Accounts', icon: '🏦', permission: 'Billing.Payments.View' },
    ],
  },
  {
    id: 'ai',
    label: 'AI',
    icon: 'M9 3v2M15 3v2M9 19v2M15 19v2M3 9h2M3 15h2M19 9h2M19 15h2M7 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM9 9h6v6H9z',
    links: [
      { path: '/ai/providers', label: 'Providers', icon: '🤖', permission: 'Platform.Secrets.Manage' },
      { path: '/ai/models', label: 'Models', icon: '🧠', permission: 'Platform.Ai.Routing' },
      { path: '/ai/assignments', label: 'Assignments', icon: '🎯', permission: 'Platform.Ai.Routing' },
      // AI images add-on usage (specs/agents CONTRACT-v1.4 §9).
      { path: '/ai/images', label: 'Images', icon: '🖼️', permission: 'Platform.Ai.Routing' },
      // Agents, Plus Intelligence and Micro apps, on or off for every business (specs/agents spec-v1.7).
      { path: '/ai/switches', label: 'Feature Switches', icon: '🎚️', permission: 'Platform.Ai.Switches' },
      { path: '/ai/status', label: 'Configuration Status', icon: '🩺', permission: 'Platform.Ai.Routing' },
      { path: '/agents', label: 'Agents', icon: '🛠️', permission: 'Platform.Agents.Review' },
      // specs/agents CONTRACT-v1.3 §11.
      { path: '/agents/integrity', label: 'Integrity Signals', icon: '🧪', permission: 'Platform.Agents.Review' },
      // Plus AI Studio (specs/agents v1.6 FR-72).
      { path: '/studio/reviews', label: 'Studio Reviews', icon: '🔬', permission: 'Platform.Studio.Review' },
      { path: '/studio/catalog', label: 'Studio Catalog', icon: '🧩', permission: 'Platform.Studio.Review' },
      { path: '/studio/erp-map', label: 'ERP Map', icon: '🗺️', permission: 'Platform.Studio.Review' },
    ],
  },
  {
    id: 'security',
    label: 'Security',
    icon: 'M12 3l8 3v6c0 4.5-3.2 7.8-8 9-4.8-1.2-8-4.5-8-9V6zM9 12l2 2 4-4',
    links: [
      { path: '/security', label: 'Overview', icon: '🛡️', exact: true, permission: 'Security.View' },
      { path: '/security/blocked-ips', label: 'Blocked IPs', icon: '🚫', permission: 'Security.View' },
      { path: '/security/blocked-countries', label: 'Blocked Countries', icon: '🌍', permission: 'Security.View' },
      { path: '/security/blocked-referrers', label: 'Blocked Referrers', icon: '🔗', permission: 'Security.View' },
      { path: '/security/failed-logins', label: 'Failed Logins', icon: '🔐', permission: 'Security.View' },
      { path: '/security/staff-sign-ins', label: 'Staff Sign-ins', icon: '🪪', permission: 'Security.View' },
      { path: '/security/block-events', label: 'Block Log', icon: '📋', permission: 'Security.View' },
      { path: '/security/admin-activity', label: 'Admin Activity', icon: '🧾', permission: 'Platform.Audit.View' },
      { path: '/security/settings', label: 'Settings', icon: '⚙️', permission: 'Security.Settings.Manage' },
    ],
  },
];

/**
 * Which section owns a URL. Strict first (exact links only match exactly), then by prefix so a
 * detail page or a route that has no menu entry still lights up its section.
 */
export function sectionFor(url: string, sections: NavSection[] = SECTIONS): string | null {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const under = (p: string) => path === p || path.startsWith(p + '/');
  let best: { id: string; len: number } | null = null;
  const consider = (id: string, p: string) => {
    if (!best || p.length > best.len) best = { id, len: p.length };
  };
  for (const s of sections) for (const l of s.links) {
    if (l.exact ? path === l.path : under(l.path)) consider(s.id, l.path);
  }
  if (!best) {
    for (const s of sections) for (const l of s.links) {
      if (under(l.path)) consider(s.id, l.path);
    }
  }
  return best ? (best as { id: string }).id : null;
}

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
})
export class Sidebar implements OnInit, OnDestroy {
  /** Permission-filtered sections. Recomputed on navigation and whenever staff access loads. */
  protected readonly sections = signal<NavSection[]>([]);
  /** The Home tile, or null without its permission. */
  protected readonly home = signal<NavLink | null>(null);
  protected readonly displayName = signal('');
  protected readonly initials = computed(() => {
    const parts = this.displayName().trim().split(/[\s@._-]+/).filter(Boolean);
    return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase();
  });

  /** The section whose flyout is open, if any. */
  protected readonly openId = signal<string | null>(null);
  /** The section that owns the current page; it keeps its marker while another flyout is open. */
  protected readonly currentId = signal<string | null>(null);

  /** The saved preference: keep the second level open as a column beside the rail. */
  protected readonly pinned = signal(readPinned());
  /** Wider than the 720px breakpoint, where the menu is a left rail rather than a top strip. */
  private readonly wide = signal(true);
  /** Pinned only matters where there is a rail to dock against; a narrow window keeps the drop-down. */
  protected readonly docked = computed(() => this.pinned() && this.wide());
  private readonly media = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(min-width: 721px)') : null;
  private readonly onMedia = (e: MediaQueryListEvent) => this.wide.set(e.matches);

  /** What the second level shows: the section that was clicked, or, when docked, the current page's section. */
  protected readonly open = computed(() => {
    const sections = this.sections();
    const byClick = sections.find(s => s.id === this.openId());
    if (byClick) return byClick;
    if (!this.docked()) return null;
    return sections.find(s => s.id === this.currentId()) ?? sections[0] ?? null;
  });

  private navSub?: Subscription;

  /** Refund requests waiting for review. */
  protected readonly refundRequestsWaiting;

  /** Seller category requests waiting for a decision. */
  protected readonly categoryRequestsWaiting;

  /** Seller brands and brand changes waiting for staff. */
  protected readonly brandReviewWaiting;

  constructor(
    private authService: AuthService,
    private router: Router,
    private host: ElementRef<HTMLElement>,
    private refundRequests: MarketplaceRefundRequestsService,
    private categoryRequests: CategoryRequestsAdminService,
    private brandReview: BrandReviewAdminService
  ) {
    this.refundRequestsWaiting = this.refundRequests.pendingCount;
    this.categoryRequestsWaiting = this.categoryRequests.pendingCount;
    this.brandReviewWaiting = this.brandReview.waitingCount;
    effect(() => { this.authService.staffAccess(); this.refresh(); });
  }

  ngOnInit(): void {
    if (this.media) {
      this.wide.set(this.media.matches);
      this.media.addEventListener('change', this.onMedia);
    }
    this.refresh();
    this.navSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    this.media?.removeEventListener('change', this.onMedia);
    this.navSub?.unsubscribe();
    this.refundRequests.stopPolling();
    this.categoryRequests.stopPolling();
    this.brandReview.stopPolling();
  }

  /** Open a section's flyout, or close it when it is the one already open (a docked column stays). */
  toggleSection(id: string): void {
    if (this.open()?.id === id) {
      if (!this.docked()) this.close();
      return;
    }
    this.openId.set(id);
    if (!this.docked()) {
      // Move focus into the flyout so the arrow keys and Escape work from the keyboard.
      setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>('.flyout')?.focus(), 0);
    }
  }

  /** Close the flyout. Picking a destination, Escape and the scrim all land here; a docked column ignores it. */
  close(): void {
    if (this.docked()) return;
    const wasOpen = this.openId();
    this.openId.set(null);
    if (wasOpen) {
      // Hand focus back to the tile that opened it.
      setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(`[data-section="${wasOpen}"]`)?.focus(), 0);
    }
  }

  /** Pin the second level open as a column, or let it go back to a flyout (which closes). */
  togglePin(): void {
    const next = !this.pinned();
    this.pinned.set(next);
    if (!next) this.openId.set(null);
    try { localStorage.setItem(PINNED_KEY, next ? '1' : '0'); } catch { /* convenience only */ }
  }

  /** The count on a section's tile: everything waiting across its destinations. */
  waiting(s: NavSection): number {
    let n = 0;
    for (const l of s.links) {
      if (l.badge === 'refundRequests') n += this.refundRequestsWaiting();
      else if (l.badge === 'categoryRequests') n += this.categoryRequestsWaiting();
      else if (l.badge === 'brandReview') n += this.brandReviewWaiting();
    }
    return n;
  }

  badgeFor(l: NavLink): number {
    switch (l.badge) {
      case 'refundRequests': return this.refundRequestsWaiting();
      case 'categoryRequests': return this.categoryRequestsWaiting();
      case 'brandReview': return this.brandReviewWaiting();
      default: return 0;
    }
  }

  /**
   * Escape closes an undocked flyout. Ctrl+B (Cmd+B on a Mac) pins and unpins the second level,
   * like the old collapse shortcut, except while typing, where it means bold.
   */
  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.openId() && !this.docked()) {
      e.preventDefault();
      this.close();
      return;
    }
    const t = e.target as HTMLElement | null;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    if (this.wide() && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      this.togglePin();
    }
  }

  onLogout(): void {
    this.authService.logOut();
  }

  /** Rebuild the visible menu and the footer identity from the current token. */
  private refresh(): void {
    const visible = SECTIONS
      .map(s => ({ ...s, links: s.links.filter(l => this.authService.can(l.permission)) }))
      .filter(s => s.links.length > 0);
    this.sections.set(visible);
    this.home.set(this.authService.can(HOME.permission) ? HOME : null);
    this.currentId.set(sectionFor(this.router.url, visible));
    if (this.openId() && !visible.some(s => s.id === this.openId())) this.openId.set(null);

    // Poll the badge only for someone who can see (and act on) the queue.
    if (visible.some(s => s.links.some(l => l.badge === 'refundRequests'))) {
      this.refundRequests.startPolling();
    } else {
      this.refundRequests.stopPolling();
    }
    if (visible.some(s => s.links.some(l => l.badge === 'categoryRequests'))) {
      this.categoryRequests.startPolling();
    } else {
      this.categoryRequests.stopPolling();
    }
    if (visible.some(s => s.links.some(l => l.badge === 'brandReview'))) {
      this.brandReview.startPolling();
    } else {
      this.brandReview.stopPolling();
    }

    const claims = (this.authService.getClaims() ?? {}) as Record<string, any>;
    const name = claims['name'] ?? claims['given_name'] ?? claims['preferred_username'] ?? claims['email'] ?? '';
    this.displayName.set(String(name));
  }
}

function readPinned(): boolean {
  try { return localStorage.getItem(PINNED_KEY) === '1'; } catch { return false; }
}
